/**
 * Slice 49 / O1 — Unit tab → Location stamps sheet.
 * Three decisions only: on/off, where the property is, how far counts as "away".
 * Turning it off keeps Start clean and the times; it just never asks for location.
 */
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  AWAY_DISTANCE_OPTIONS_M,
  DEFAULT_AWAY_DISTANCE_M,
  normalizeAwayDistanceM,
  stampMapUrl,
} from "@/lib/locationStamps";

type PropertyStampRow = {
  address: string | null;
  formatted_address: string | null;
  latitude: number | null;
  longitude: number | null;
  location_stamps_enabled: boolean | null;
  location_away_distance_m: number | null;
};

export function awayLabel(m: number): string {
  return m >= 1000 ? `${m / 1000} km` : `${m} m`;
}

/** One-line summary for the Unit tab row: "On · flag if farther than 300 m" or "Off". */
export function useLocationStampsSummary(propertyId: string): string | null {
  const { data } = usePropertyStampRow(propertyId);
  if (!data) return null;
  if (data.location_stamps_enabled === false) return "Off";
  return `On · flag if farther than ${awayLabel(normalizeAwayDistanceM(data.location_away_distance_m))}`;
}

function usePropertyStampRow(propertyId: string) {
  return useQuery({
    queryKey: ["location-stamps-settings", propertyId],
    queryFn: async (): Promise<PropertyStampRow | null> => {
      const { data, error } = await supabase
        .from("properties")
        .select("address, formatted_address, latitude, longitude, location_stamps_enabled, location_away_distance_m")
        .eq("id", propertyId)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as PropertyStampRow | null) ?? null;
    },
  });
}

function parseCoordinates(input: string): { lat: number; lng: number } | null {
  const m = input.trim().match(/^(-?\d{1,2}(?:\.\d+)?)\s*[, ]\s*(-?\d{1,3}(?:\.\d+)?)$/);
  if (!m) return null;
  const lat = Number(m[1]);
  const lng = Number(m[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

export function LocationStampsSettings({ propertyId, onSaved }: { propertyId: string; onSaved?: () => void }) {
  const queryClient = useQueryClient();
  const { data: row, isLoading } = usePropertyStampRow(propertyId);
  const [enabled, setEnabled] = useState(true);
  const [awayM, setAwayM] = useState<number>(DEFAULT_AWAY_DISTANCE_M);
  const [pin, setPin] = useState<{ lat: number; lng: number } | null>(null);
  const [movingPin, setMovingPin] = useState(false);
  const [pasted, setPasted] = useState("");
  const [locating, setLocating] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!row) return;
    setEnabled(row.location_stamps_enabled !== false);
    setAwayM(normalizeAwayDistanceM(row.location_away_distance_m));
    setPin(
      row.latitude != null && row.longitude != null
        ? { lat: Number(row.latitude), lng: Number(row.longitude) }
        : null,
    );
  }, [row]);

  const address = row?.formatted_address || row?.address || "No address saved";

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast({ title: "This browser can't share its location", variant: "destructive" });
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPin({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setMovingPin(false);
        setLocating(false);
      },
      () => {
        setLocating(false);
        toast({ title: "Couldn't read your location", variant: "destructive" });
      },
      { enableHighAccuracy: true, timeout: 15_000 },
    );
  };

  const applyPasted = () => {
    const parsed = parseCoordinates(pasted);
    if (!parsed) {
      toast({ title: "Paste coordinates like 43.6426, -79.3871", variant: "destructive" });
      return;
    }
    setPin(parsed);
    setPasted("");
    setMovingPin(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        location_stamps_enabled: enabled,
        location_away_distance_m: awayM,
      };
      if (pin) {
        payload.latitude = pin.lat;
        payload.longitude = pin.lng;
      }
      const { error } = await supabase.from("properties").update(payload as never).eq("id", propertyId);
      if (error) throw error;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["location-stamps-settings", propertyId] }),
        queryClient.invalidateQueries({ queryKey: ["stamp-settings", propertyId] }),
        queryClient.invalidateQueries({ queryKey: ["property", propertyId] }),
        queryClient.invalidateQueries({ queryKey: ["properties"] }),
      ]);
      onSaved?.();
    } catch (err) {
      toast({
        title: "Couldn't save",
        description: err instanceof Error ? err.message : "Try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-10 text-[#64748b]">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[22px]">
      <div className="text-[13px] font-medium text-[#8494a2]">Saved at Start clean and Finish only</div>

      <div className="flex items-center gap-4 rounded-[14px] border border-[#e6edf1] px-[18px] py-4">
        <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
          <div className="text-[16px] font-bold text-[#0f1720]">Save location at start and finish</div>
          <div className="text-[13px] font-medium leading-[1.45] text-[#8494a2]">
            Off: times are still saved, no location is asked for.
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label="Save location at start and finish"
          onClick={() => setEnabled((v) => !v)}
          className={cn(
            "flex h-[30px] w-[50px] shrink-0 rounded-full p-[3px]",
            enabled ? "justify-end bg-[#24a89a]" : "justify-start bg-[#cdd8df]",
          )}
        >
          <span className="h-6 w-6 rounded-full bg-white" />
        </button>
      </div>

      <div className="flex flex-col gap-2">
        <div className="text-[11px] font-bold tracking-[0.8px] text-[#8494a2]">PROPERTY ADDRESS</div>
        <a
          href={pin ? stampMapUrl(pin.lat, pin.lng) : undefined}
          target="_blank"
          rel="noopener noreferrer"
          className="relative flex h-[150px] items-center justify-center rounded-[14px] border border-[#e6edf1] bg-[repeating-linear-gradient(135deg,#eef3f6_0_8px,#e3eaef_8px_16px)]"
        >
          <span className="h-[22px] w-[22px] rounded-full border-4 border-white bg-[#24a89a] shadow-[0_2px_8px_rgba(15,23,32,0.25)]" />
          <span className="absolute bottom-2.5 left-3 rounded-[7px] bg-white px-2 py-1 font-mono text-[10px] text-[#64748b]">
            {pin ? `${pin.lat.toFixed(5)}, ${pin.lng.toFixed(5)} · open map` : "no pin yet"}
          </span>
        </a>
        <div className="flex items-center gap-2.5">
          <div className="min-w-0 flex-1 text-[15px] font-bold text-[#0f1720]">{address}</div>
          <button
            type="button"
            onClick={() => setMovingPin((v) => !v)}
            className="text-[13px] font-bold text-[#15776d]"
          >
            Move pin
          </button>
        </div>
        {movingPin ? (
          <div className="flex flex-col gap-2 rounded-[12px] border border-[#e6edf1] bg-[#f4f7f9] p-3">
            <button
              type="button"
              onClick={useMyLocation}
              disabled={locating}
              className="flex h-10 items-center justify-center rounded-[10px] bg-white text-[14px] font-bold text-[#15776d] border border-[#cfe4e1] disabled:opacity-60"
            >
              {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Use my location as the pin"}
            </button>
            <div className="flex gap-2">
              <input
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                placeholder="Or paste coordinates: 43.6426, -79.3871"
                className="h-10 min-w-0 flex-1 rounded-[10px] border border-[#d7e0e6] bg-white px-3 text-[14px]"
              />
              <button
                type="button"
                onClick={applyPasted}
                className="h-10 rounded-[10px] bg-white px-3 text-[14px] font-bold text-[#15776d] border border-[#cfe4e1]"
              >
                Set
              </button>
            </div>
          </div>
        ) : null}
        <div className="text-[12.5px] font-medium leading-[1.5] text-[#8494a2]">
          Stamps are measured from this pin. Move it if the entrance is far from the street address.
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="text-[11px] font-bold tracking-[0.8px] text-[#8494a2]">FLAG AS AWAY IF FARTHER THAN</div>
        <div className="flex flex-wrap gap-2">
          {AWAY_DISTANCE_OPTIONS_M.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setAwayM(m)}
              className={cn(
                "h-10 rounded-full border-[1.5px] px-4 text-[14px]",
                awayM === m
                  ? "border-[#24a89a] bg-[#e2eeed] font-bold text-[#15776d]"
                  : "border-[#e6edf1] font-medium text-[#475569] hover:border-[#b0bcc4]",
              )}
            >
              {awayLabel(m)}
            </button>
          ))}
        </div>
        <div className="text-[12.5px] font-medium leading-[1.5] text-[#8494a2]">
          Phone location can be off by a block in cities. 300 m avoids false flags.
        </div>
      </div>

      <button
        type="button"
        onClick={() => void save()}
        disabled={saving}
        className="flex h-[50px] items-center justify-center rounded-[13px] bg-[#24a89a] text-[16px] font-bold text-white hover:bg-[#1b8378] disabled:opacity-60"
      >
        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save"}
      </button>
    </div>
  );
}
